import {
  UpdatePortfolioRequest,
  PortfolioResult,
  PortfolioService,
} from '@/api/';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useStore } from '@nanostores/react';
import { DotsHorizontalIcon } from '@radix-ui/react-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  Loader2,
  Pencil,
  Trash2Icon,
  User2Icon,
  Users2Icon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { DeletePortfolioConfirmation } from './DeletePortfolioConfirmation';

/**
 * We'll update the selected portfolio in the store and route
 * to the projects for this portfolio.
 */
import { ReactNode } from 'react';

function SortableItem({ id, children }: { id: string; children: ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
}

function handleViewAccountReceivables(orgId: string, ws: PortfolioResult) {
  $portfolio.set(ws);

  openPage($router, 'accountreceivables', {
    organizationId: orgId,
    portfolioId: ws.portfolio.id,
  });
}

// opens the portfolioCollaborators page
function handleManageCollaborators(orgId: string, ws: PortfolioResult) {
  $portfolio.set(ws);

  openPage($router, 'portfolioCollaborators', {
    organizationId: orgId,
    portfolioId: ws.portfolio.id,
  });
}

export type PortfolioListingProps = {
  organizationId: string;
};

export const PortfolioListing = ({ organizationId }: PortfolioListingProps) => {
  const org = useStore($org);
  const queryClient = useQueryClient();
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  );

  const [isDeletePortfolioModalOpen, setIsDeletePortfolioModalOpen] =
    useState(false);
  const [portfolioToBeDeleted, setPortfolioToBeDeleted] = useState('');

  // State to manage editing portfolio name
  const [editingPortfolioName, setEditingPortfolioName] = useState(false);
  const [portfolioName, setPortfolioName] = useState('');

  // Query to get the portfolios of the organization
  const portfolios = useQuery<PortfolioResult[]>({
    queryKey: ['getPortfolios', organizationId],
    enabled: !!organizationId,
    queryFn: async () => {
      const res = await PortfolioService.getPortfolios(organizationId);
      return res;
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  function constructOrderMap(orders: string[]): Map<string, number> {
    const orderMap = new Map<string, number>();
    orders.forEach((item, index) => {
      orderMap.set(item, index);
    });
    return orderMap;
  }

  function sortPortfoliosUsingSavedOrders(
    items: PortfolioResult[],
  ): PortfolioResult[] {
    const savedPortfolioOrders = JSON.parse(
      localStorage.getItem('portfolios_orders') || '{}',
    );

    if (Object.keys(savedPortfolioOrders)?.includes(organizationId)) {
      const portfolioOrder = savedPortfolioOrders[organizationId];

      // construct the order map
      const portfolioOrderMap = constructOrderMap(portfolioOrder);
      return items.sort((a, b) => {
        const orderA =
          portfolioOrderMap.get(a?.portfolio?.id) ?? Number.MAX_SAFE_INTEGER;
        const orderB =
          portfolioOrderMap.get(b?.portfolio?.id) ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      });
    }

    return items;
  }

  // Mutation to update the portfolio details
  const updatePortfolio = useMutation({
    mutationFn: async (payload: UpdatePortfolioRequest) => {
      if (!portfolioToBeDeleted) {
        throw new Error('No portfolio selected for update.');
      }

      const res = await PortfolioService.updatePortfolio(
        portfolioToBeDeleted,
        payload,
      );
      return res;
    },
    onSuccess: async data => {
      // Ensure data.portfolio is valid
      if (!data?.portfolio) {
        console.error('Error: data.portfolio is undefined.');
        return; // Safeguard against undefined data.portfolio
      }

      // Update the store with the new portfolio data
      $portfolio.set({
        ...(selectedWs || {
          invites: 0,
          collaborators: [],
          accountReceivables: [],
          accountReceivablesCount: 0
        }),
        portfolio: data.portfolio,
        accountReceivablesCount: data.portfolio.accountReceivablesCount || 0,
      });

      setEditingPortfolioName(false);

      // Invalidate and refetch updated data
      await queryClient.invalidateQueries({
        queryKey: ['getPortfolios', organizationId],
      });
    },
    onError: error => {
      console.error(error);
      toast('An error occurred', {
        description: error.message,
        duration: 5000,
      });
    },
  });

  // Load portfolio order from local storage
  useEffect(() => {
    if (portfolios?.isSuccess) {
      if (portfolios?.data?.length > 0) {
        $portfolios.set(portfolios?.data);
      }
    }
  }, [organizationId]);
  const selectedWs = useStore($portfolio);
  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const updatedPortfolios = (items: PortfolioResult[]) => {
        const oldIndex = items.findIndex(
          item => item.portfolio.id === active.id,
        );
        const newIndex = items.findIndex(item => item.portfolio.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      };

      // Update the portfolio order in the local query cache
      queryClient.setQueryData(
        ['getPortfolios', organizationId],
        updatedPortfolios,
      );
      const newPortfolios = updatedPortfolios(portfolios.data || []);

      // update on the local storage
      const savedPortfolioOrders = JSON.parse(
        localStorage.getItem('portfolios_orders') || '{}',
      );

      if (Object.keys(savedPortfolioOrders)?.includes(organizationId)) {
        savedPortfolioOrders[organizationId] = newPortfolios?.map(
          ws => ws.portfolio.id,
        );
        localStorage.setItem(
          'portfolios_orders',
          JSON.stringify(savedPortfolioOrders),
        );
      }
    }
  };

  return (
    <>
      <ScrollArea className="h-[calc(100vh-200px)]">
        <div className="flex flex-col gap-2 pr-4">
          {portfolios.isLoading && (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}

          {portfolios.data?.length === 0 && (
            <div className="flex h-32 items-center justify-center">
              <p>No account groups found.</p>
            </div>
          )}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={portfolios.data?.map(ws => ws.portfolio.id) || []}
              strategy={verticalListSortingStrategy}
            >
              {sortPortfoliosUsingSavedOrders(portfolios?.data || [])?.map(
                ws => (
                  <SortableItem key={ws.portfolio.id} id={ws.portfolio.id}>
                    <Card
                      className={cn(
                        selectedWs?.portfolio.id === ws.portfolio.id &&
                        'ring-primary-muted ring-1 ring-inset',
                      )}
                      key={ws.portfolio?.id}
                    >
                      <CardHeader className="flex flex-row gap-4 space-y-0">
                        <div className="flex flex-grow items-center gap-2">
                          <Avatar className="h-10 w-10">
                            <AvatarFallback>
                              {ws.portfolio.name
                                .split(' ')
                                .map((n) => n[0])
                                .join('')
                                .toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="space-y-1">
                            {editingPortfolioName &&
                              portfolioToBeDeleted === ws.portfolio.id ? (
                              <Input
                                value={portfolioName}
                                onChange={e => setPortfolioName(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    updatePortfolio.mutateAsync({
                                      name: portfolioName,
                                    });
                                  }
                                }}
                                className="min-w-[300px]"
                              />
                            ) : (
                              <CardTitle>
                                <span
                                  className="cursor-pointer text-muted-foreground hover:text-blue-700"
                                  onClick={() =>
                                    handleViewAccountReceivables(org?.id ?? 'select-an-org', ws)
                                  }
                                >
                                  {ws.portfolio.name}
                                </span>
                              </CardTitle>
                            )}
                            <CardDescription>
                              {ws.portfolio.description}
                            </CardDescription>
                            <div className="text-sm text-muted-foreground">
                              Number of Accounts: {ws.accountReceivablesCount}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-none gap-2">
                          <Button
                            variant="outline"
                            className=""
                            onClick={() =>
                              handleViewAccountReceivables(org?.id ?? 'select-an-org', ws)
                            }
                          >
                            View Accounts
                          </Button>

                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" size="icon">
                                <DotsHorizontalIcon className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="w-[200px]"
                              forceMount
                            >
                              <DropdownMenuLabel>Options</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() =>
                                  handleManageCollaborators(
                                    org?.id ?? 'select-an-org',
                                    ws,
                                  )
                                }
                              >
                                <Users2Icon className="mr-2 h-4 w-4" /> Manage
                                Collaborators
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setEditingPortfolioName(true);
                                  setPortfolioToBeDeleted(ws.portfolio.id);
                                  setPortfolioName(ws.portfolio.name);
                                }}
                              >
                                <Pencil className="mr-2 h-4 w-4" /> Edit Name
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setPortfolioToBeDeleted(ws.portfolio.id);
                                  setIsDeletePortfolioModalOpen(true);
                                }}
                              >
                                <Trash2Icon className="mr-2 h-4 w-4" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </CardHeader>
                      {editingPortfolioName &&
                        portfolioToBeDeleted === ws.portfolio.id && (
                          <div className="flex justify-end gap-2 p-4">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingPortfolioName(false);
                                setPortfolioName(ws.portfolio.name);
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                updatePortfolio.mutateAsync({
                                  name: portfolioName,
                                });
                              }}
                            >
                              {updatePortfolio.isPending ? (
                                <Loader2 className="ml-0 h-3 animate-spin p-0" />
                              ) : (
                                'Save'
                              )}
                            </Button>
                          </div>
                        )}
                      <CardContent>
                        <div className="flex space-x-4 text-sm text-muted-foreground">
                          <div className="flex items-center">
                            <Users2Icon className="mr-1 h-3 w-3" />
                            {ws.collaborators.length} Collaborators
                          </div>
                          {ws.portfolio.createdBy && (
                            <div className="flex items-center">
                              <User2Icon className="mr-1 h-3 w-3" />
                              Created by: {ws.portfolio.createdBy.name || 'N/A'}
                            </div>
                          )}

                          <div className="flex items-center">
                            <Calendar className="mr-1 h-3 w-3" />
                            Created on{' '}
                            {new Date(
                              ws.portfolio.createdAtUtc,
                            ).toLocaleDateString()}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </SortableItem>
                ),
              )}
            </SortableContext>
          </DndContext>
        </div>
        <DeletePortfolioConfirmation
          isOpen={isDeletePortfolioModalOpen}
          handleOpen={setIsDeletePortfolioModalOpen}
          portfolioId={portfolioToBeDeleted}
          organizationId={organizationId}
        />
      </ScrollArea>
    </>
  );
};
